import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Calculator, Plus, Truck } from "lucide-react";
import { toast } from "sonner";

const round3 = (v) => Math.round((Number(v || 0) + Number.EPSILON) * 1000) / 1000;
const formatKg = (v) => Number(v || 0).toFixed(3);

const ScrapDispatch = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [parties, setParties] = useState([]);
  const [batches, setBatches] = useState([]);
  const [availableCoils, setAvailableCoils] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState({});
  const [manualAllocations, setManualAllocations] = useState({});
  const [createdBatch, setCreatedBatch] = useState(null);
  const [form, setForm] = useState({
    party_id: "",
    dispatch_date: new Date().toISOString().slice(0, 10),
    total_scrap_dispatched_kg: "",
    allocation_mode: "PRO_RATA",
    remarks: "",
  });

  const selectedCoils = useMemo(() => {
    return availableCoils.filter((c) => selectedKeys[`${c.coil_in_id}::${c.coil_id}`]);
  }, [availableCoils, selectedKeys]);

  const manualTotal = useMemo(() => {
    return round3(
      selectedCoils.reduce((sum, c) => {
        const key = `${c.coil_in_id}::${c.coil_id}`;
        return sum + Number(manualAllocations[key] || 0);
      }, 0)
    );
  }, [selectedCoils, manualAllocations]);

  const loadInitial = async () => {
    setLoading(true);
    try {
      const [partyRes, batchRes] = await Promise.all([
        api.parties.list({ is_active: true }),
        api.scrapDispatchBatches.list({ limit: 200 }),
      ]);
      setParties(partyRes.data || []);
      setBatches(batchRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load scrap dispatch data");
    } finally {
      setLoading(false);
    }
  };

  const loadPartyCoils = async (partyId) => {
    if (!partyId) {
      setAvailableCoils([]);
      setSelectedKeys({});
      setManualAllocations({});
      return;
    }
    try {
      const coilInRes = await api.coilIns.list({ is_active: true });
      const partyCoilIns = (coilInRes.data || []).filter((ci) => ci.party_id === partyId);
      const coilRefs = [];
      for (const ci of partyCoilIns) {
        for (const c of ci.coils || []) {
          coilRefs.push({
            coil_in_id: ci.id,
            coil_id: c.coil_id,
            inward_date: ci.inward_datetime,
            slip_no: ci.slip_no,
          });
        }
      }

      const summaries = await Promise.all(
        coilRefs.map(async (ref) => {
          try {
            const res = await api.reconciliation.getSummary(ref.coil_in_id, ref.coil_id);
            const s = res.data;
            return {
              ...ref,
              final_scrap_weight_kg: s?.final_scrap_weight_kg,
              scrap_dispatched_kg: s?.scrap_dispatched_kg || 0,
              scrap_pending_kg: s?.scrap_pending_kg,
              lifecycle_state: s?.reconciliation_status,
            };
          } catch {
            return null;
          }
        })
      );

      const filtered = (summaries || [])
        .filter(Boolean)
        .filter((x) => x.final_scrap_weight_kg != null && Number(x.scrap_pending_kg || 0) > 0.0001);

      setAvailableCoils(filtered);
      setSelectedKeys({});
      setManualAllocations({});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load coils for party");
    }
  };

  useEffect(() => {
    loadInitial();
  }, []);

  useEffect(() => {
    loadPartyCoils(form.party_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.party_id]);

  const toggleCoil = (coilInId, coilId, checked) => {
    const key = `${coilInId}::${coilId}`;
    setSelectedKeys((prev) => ({ ...prev, [key]: !!checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const total = Number(form.total_scrap_dispatched_kg);
    if (!form.party_id) return toast.error("Select party");
    if (!Number.isFinite(total) || total <= 0) return toast.error("Total scrap dispatched must be > 0");
    if (selectedCoils.length === 0) return toast.error("Select at least one coil/lot");

    const payload = {
      party_id: form.party_id,
      dispatch_date: form.dispatch_date ? new Date(form.dispatch_date).toISOString() : new Date().toISOString(),
      total_scrap_dispatched_kg: total,
      allocation_mode: form.allocation_mode,
      remarks: form.remarks || null,
    };

    if (form.allocation_mode === "MANUAL") {
      const allocations = selectedCoils.map((c) => {
        const key = `${c.coil_in_id}::${c.coil_id}`;
        return {
          coil_in_id: c.coil_in_id,
          coil_id: c.coil_id,
          allocated_scrap_kg: Number(manualAllocations[key] || 0),
        };
      });
      const sum = round3(allocations.reduce((acc, a) => acc + Number(a.allocated_scrap_kg || 0), 0));
      if (Math.abs(sum - round3(total)) > 0.001) {
        return toast.error(`Manual allocation total (${formatKg(sum)}) must match batch total (${formatKg(total)})`);
      }
      payload.allocations = allocations;
    } else {
      payload.selected_coils = selectedCoils.map((c) => ({ coil_in_id: c.coil_in_id, coil_id: c.coil_id }));
    }

    setSubmitting(true);
    try {
      const res = await api.scrapDispatchBatches.create(payload);
      setCreatedBatch(res.data);
      toast.success("Canonical scrap dispatch created");
      setForm((prev) => ({ ...prev, total_scrap_dispatched_kg: "", remarks: "" }));
      setSelectedKeys({});
      setManualAllocations({});
      await loadInitial();
      await loadPartyCoils(form.party_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create scrap dispatch");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-industrial-bg text-slate-500 font-mono text-xs uppercase p-6">Loading scrap dispatch environment...</div>;
  }

  return (
    <div className="min-h-screen bg-industrial-bg text-white p-6 space-y-6" data-testid="scrap-dispatch-page">
      {/* Header */}
      <div className="border-b border-industrial-border pb-4">
        <h1 className="font-heading text-3xl font-bold tracking-tight uppercase">Scrap Dispatch (Canonical)</h1>
        <p className="text-slate-400 mt-2 font-mono text-xs uppercase">Create coil-wise or collective scrap dispatch for final accounts.</p>
      </div>

      {/* Form Card */}
      <div className="bg-industrial-s1 border border-industrial-border p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-industrial-border pb-3">
          <Plus className="h-4 w-4 text-slate-400" />
          <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 font-bold">Create Scrap Dispatch Batch</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-bold">Party *</label>
              <select
                value={form.party_id}
                onChange={(e) => setForm((p) => ({ ...p, party_id: e.target.value }))}
                className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                required
              >
                <option value="">Select party</option>
                {parties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-bold">Dispatch Date *</label>
              <input 
                type="date" 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={form.dispatch_date} 
                onChange={(e) => setForm((p) => ({ ...p, dispatch_date: e.target.value }))} 
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-bold">Total Scrap Dispatched (kg) *</label>
              <input
                type="number"
                step="0.001"
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={form.total_scrap_dispatched_kg}
                onChange={(e) => setForm((p) => ({ ...p, total_scrap_dispatched_kg: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-bold">Allocation Mode *</label>
              <select
                value={form.allocation_mode}
                onChange={(e) => setForm((p) => ({ ...p, allocation_mode: e.target.value }))}
                className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                required
              >
                <option value="PRO_RATA">PRO_RATA</option>
                <option value="MANUAL">MANUAL</option>
              </select>
            </div>
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-bold">Remarks</label>
              <input 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={form.remarks} 
                onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))} 
              />
            </div>
          </div>

          {/* Coils lot select section */}
          <div className="border border-industrial-border bg-industrial-s2/20 p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-industrial-border/60 pb-2">
              <h3 className="font-mono font-bold text-white text-xs uppercase">Select Coils / Lots</h3>
              <span className="font-mono text-[9px] text-slate-400 border border-industrial-border bg-industrial-s2 px-2 py-0.5 uppercase">
                {selectedCoils.length} SELECTED
              </span>
            </div>
            
            {availableCoils.length === 0 ? (
              <p className="text-xs text-slate-500 font-mono uppercase">No coils with pending scrap for selected party.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-auto pr-1">
                {availableCoils.map((c) => {
                  const key = `${c.coil_in_id}::${c.coil_id}`;
                  const checked = !!selectedKeys[key];
                  return (
                    <div key={key} className="flex items-center justify-between border border-industrial-border bg-industrial-s2/40 p-3 hover:bg-industrial-s2/65 transition-colors">
                      <div className="flex items-center gap-3">
                        <input 
                          type="checkbox"
                          checked={checked} 
                          onChange={(e) => toggleCoil(c.coil_in_id, c.coil_id, e.target.checked)} 
                          className="h-4 w-4 rounded-none border-industrial-border bg-industrial-s2 accent-accent text-accent focus:ring-0 focus:outline-none"
                        />
                        <div className="font-mono">
                          <p className="text-xs font-bold text-white uppercase">Coil: {c.coil_id}</p>
                          <p className="text-[10px] text-slate-400">
                            INWARD: {new Date(c.inward_date).toLocaleDateString()} {c.slip_no ? `• SLIP: ${c.slip_no}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="text-right font-mono">
                        <p className="text-[9px] text-slate-500 uppercase">Pending Scrap</p>
                        <p className="font-semibold text-white">{formatKg(c.scrap_pending_kg)} kg</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Manual Allocations input grid */}
          {form.allocation_mode === "MANUAL" && selectedCoils.length > 0 && (
            <div className="border border-industrial-border bg-industrial-s2/20 p-4 space-y-3">
              <div className="flex items-center justify-between border-b border-industrial-border/60 pb-2">
                <h3 className="font-mono font-bold text-white text-xs uppercase">Manual Allocations</h3>
                
                <span className={`font-mono text-xs px-2 py-0.5 border uppercase font-bold ${
                  Math.abs(manualTotal - Number(form.total_scrap_dispatched_kg || 0)) <= 0.001
                    ? "border-led-green text-led-green bg-industrial-s2"
                    : "border-led-red text-led-red bg-industrial-s2"
                }`}>
                  TOTAL: {formatKg(manualTotal)} KG
                </span>
              </div>
              
              <div className="space-y-2.5">
                {selectedCoils.map((c) => {
                  const key = `${c.coil_in_id}::${c.coil_id}`;
                  return (
                    <div key={key} className="grid grid-cols-1 md:grid-cols-3 gap-2 items-center font-mono py-1 border-b border-industrial-border/30">
                      <div className="text-xs text-white uppercase font-bold">COIL ID: {c.coil_id}</div>
                      <div className="text-[10px] text-slate-400">PENDING: {formatKg(c.scrap_pending_kg)} kg</div>
                      <input
                        type="number"
                        step="0.001"
                        placeholder="Allocated kg"
                        className="w-full h-9 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                        value={manualAllocations[key] || ""}
                        onChange={(e) => setManualAllocations((prev) => ({ ...prev, [key]: e.target.value }))}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button 
              type="submit" 
              disabled={submitting}
              className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? "CREATING..." : "CREATE DISPATCH BATCH"}
            </button>
          </div>
        </form>
      </div>

      {/* Latest Allocation result */}
      {createdBatch && (
        <div className="border-l-4 border-led-green bg-industrial-s1 p-6 space-y-3 border border-industrial-border">
          <h3 className="flex items-center gap-2 font-mono text-xs uppercase font-bold text-led-green">
            <Calculator className="h-4 w-4 text-led-green" /> LATEST ALLOCATION RESULT
          </h3>
          <div className="space-y-2 text-xs font-mono">
            <p className="text-slate-300">
              Batch <span className="font-bold text-white uppercase">{createdBatch.id}</span> • MODE: <span className="text-white uppercase">{createdBatch.allocation_mode}</span> • TOTAL: <span className="text-white font-bold">{formatKg(createdBatch.total_scrap_dispatched_kg)} kg</span>
            </p>
            <div className="space-y-1 pt-2 border-t border-industrial-border/60">
              {(createdBatch.allocations || []).map((a) => (
                <div key={a.id} className="flex justify-between text-xs border-b border-industrial-border/30 py-1 hover:bg-industrial-s2/30 px-1">
                  <span className="text-slate-400">COIL: {a.coil_id}</span>
                  <span className="font-bold text-white">{formatKg(a.allocated_scrap_kg)} kg</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* History Card */}
      <div className="bg-industrial-s1 border border-industrial-border p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-industrial-border pb-3">
          <Truck className="h-4 w-4 text-slate-400" />
          <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 font-bold">Dispatch Batch History</h2>
        </div>

        {batches.length === 0 ? (
          <p className="text-xs text-slate-500 font-mono uppercase">No canonical scrap dispatch batches yet.</p>
        ) : (
          <div className="space-y-3">
            {batches.map((b) => (
              <div key={b.id} className="border border-industrial-border bg-industrial-s2/20 p-4 font-mono text-xs hover:bg-industrial-s2/30 transition-colors">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-industrial-border/30 pb-2.5 mb-2.5">
                  <div>
                    <p className="font-sans text-sm font-bold text-white uppercase">{b.party_name || b.party_id}</p>
                    <p className="text-[10px] text-slate-400 mt-1 uppercase">DATE: {new Date(b.dispatch_date).toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-[9px] text-slate-400 border border-industrial-border bg-industrial-s2 px-2 py-0.5 uppercase">
                      {b.allocation_mode}
                    </span>
                    <p className="font-semibold text-white mt-1 text-sm">{formatKg(b.total_scrap_dispatched_kg)} kg</p>
                  </div>
                </div>
                
                {b.remarks && (
                  <p className="text-slate-300 bg-industrial-s2 p-2 border border-industrial-border/60 mb-2.5 break-words">{b.remarks}</p>
                )}
                
                <div className="space-y-1 pl-2 border-l border-industrial-border/80">
                  {(b.allocations || []).map((a) => (
                    <div key={a.id} className="flex justify-between text-[11px] border-b border-industrial-border/20 py-0.5 hover:bg-industrial-s2/40 px-1">
                      <span className="text-slate-400">COIL: {a.coil_id}</span>
                      <span className="text-white font-bold">{formatKg(a.allocated_scrap_kg)} kg</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScrapDispatch;
